import React, { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import cx from "classnames";
import { CheckmarkFilled } from "@carbon/react/icons";

import { OverflowMenu, OverflowMenuItem } from "@carbon/react";

import styles from "./Device.module.scss";
import "./_override.scss";

import { PanelProgress } from "../Panel/Panel";
import moment from "moment";
import "moment/locale/de";

import useDeviceData from "@/lib/hooks/useDeviceStatistics";
import { sendCommand } from "@/redux/new_services/device";
import useUser from "@/lib/hooks/useUser";
import useModal from "@/lib/hooks/useModal";
import { openDeviceForm } from "@/redux/new_services/forms";
import formatLastSeen from "./formatLastSeen";

moment.locale("de");

export const Dot = ({ state = 0, style = {} }) => {
  let rowClasses = "";

  switch (state) {
    case 0:
      rowClasses = styles.dot_online;
      break;
    case 1:
      rowClasses = styles.dot_offline;
      break;
    case 2:
      rowClasses = styles.dot_error;
      break;
    default:
      rowClasses = "";
  }

  return <div className={cx(rowClasses)} style={style}></div>;
};

const Device = memo(
  ({ device, selected = false, onClick = () => {}, expanded = false, isMirror = false }) => {
    const dispatch = useDispatch();
    const [confirmRemoveDevice] = useModal("DELETE_DEVICE");
    const [showMirrorWizzard] = useModal("CREATE_MIRROR_DEVICE");

    const [uuid, setUUID] = useState(null);

    useEffect(() => {
      if (device) {
        setUUID(device.uuid);
      }
    }, [device]);

    const {
      online: micast_online,
      informations,
      memory: mqtt_memory,
      error: mqtt_error,
      lastMessage: lastConnection,
    } = useDeviceData(uuid);

    // remove debugg logs!
    //useEffect(() => {
    //  console.info("mqtt_memory", mqtt_memory);
    //}, [mqtt_memory]);

    const handleOpenMenderLink = () => {
      if (device && device.mender_id && device.mender_id != "") {
        window
          .open(`https://mender.micast.de/ui/#/devices?id=${device.mender_id}`, "_blank")
          .focus();
      }
    };

    const handeRemoveDevice = () => {
      // useModal spreads modalProps onto the modal component, so we wrap
      // the device under the `device` key the modal actually consumes.
      confirmRemoveDevice({ device });
    };

    const addMirror = (device) => {
      showMirrorWizzard({
        mirrorServerDeviceID: device.model.ID,
        mirrorServerName: device.label,
      });
    };

    const editMicast = () => {
      dispatch(openDeviceForm(device.model.ID));
    };

    const { isAdmin } = useUser();

    const changeVolume = (e) => {
      console.info("change volume", e.value);
      sendCommand(device.model.ID, "micast-dj", {
        set: "stream_volume",
        value: e.value.toString(),
      });
    };

    const isMaster = device?.mirrors && device.mirrors.length > 0;

    return (
      <div className={cx([styles.outerDevice])}>
        <div
          className={cx(
            isMirror
              ? styles.mirrorDevice
              : isMaster
              ? expanded
                ? styles.masterDevice
                : styles.device
              : styles.device,
          )}
          onClick={onClick}
        >
          <div className={cx(styles.leftSide)}>
            <Dot state={micast_online === true ? 0 : 1} />

            <div className={cx(styles.device__body)}>{device.label || device.name}</div>

            {selected && <CheckmarkFilled size={32} className={cx(styles.device__icon)} />}

            {expanded && (
              <div className={cx(styles.buttons)} onClick={(e) => e.stopPropagation()}>
                <OverflowMenu
                  aria-label="overflow-menu"
                  iconDescription="Micast-System Einstellungen"
                  size="md"
                >
                  <OverflowMenuItem itemText="Bearbeiten" onClick={editMicast} />
                  {!isMirror && (
                    <OverflowMenuItem
                      itemText="Spiegel hinzufügen"
                      onClick={() => {
                        addMirror(device);
                      }}
                    />
                  )}
                  {isAdmin && (
                    <OverflowMenuItem
                      hasDivider
                      itemText="Mender Öffnen"
                      onClick={() => handleOpenMenderLink()}
                    />
                  )}
                  <OverflowMenuItem
                    hasDivider
                    isDelete
                    itemText="Gerät löschen"
                    onClick={() => handeRemoveDevice()}
                  />
                </OverflowMenu>
              </div>
            )}
          </div>
          {expanded && (
            <div className={cx(styles.rightSide)}>
              <div className="info">
                <div className="info info_label">Hostname:</div>
                <div className="info info_value">{device.name}</div>
                <div className="info info_end"></div>
              </div>
              <div className="info">
                <div className="info info_label">Mac Adresse:</div>
                <div className="info info_value">{device.mac_address}</div>
                <div className="info info_end"></div>
              </div>

              <div className="info">
                <div className="info info_label">Version:</div>
                <div className="info info_value">{informations.version}</div>
                <div className="info info_end"></div>
              </div>

              {/* Speicher-Anzeige vorerst ausgeblendet — die Werte kommen
                  zeitweise als total=0 / used=0 rein und sind dann
                  irreführend. Reaktivieren, sobald die Memory-Topics
                  zuverlässig befüllt sind. */}

              {(device.network || []).map((network, index) => (
                <div className="info" key={`${device.model.ID}_${network.name}_${index}`}>
                  <div className="info info_label">{network.name}:</div>
                  <div className="info info_value">{network.ip}</div>
                  <div className="info info_end"></div>
                </div>
              ))}

              <div className="info">
                <div className="info info_label">Letzte Meldung:</div>
                <div className="info info_value"></div>
                <div className="info info_value">
                  {formatLastSeen(lastConnection, device.last_connection, micast_online)}
                </div>
                <div className="info info_end"></div>
              </div>
            </div>
          )}
        </div>
        {expanded && device.mirrors && device.mirrors.length > 0 && (
          <div className={cx(styles.mirrors)}>
            <div className={cx(styles.mirrorsHeader)}>Spiegel Systeme</div>
            {(device.mirrors || []).map((sub_device) => (
              <Device
                expanded={true}
                isMirror={true}
                device={sub_device}
                mirror_server_id={device.model.ID}
                key={`mirror_device_${sub_device.model.ID}`}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

export default Device;
